import { ReactNode, cloneElement, isValidElement, useRef } from 'react';
import cn from 'classnames';

import type { IPanelInteraction } from './types';
import styles from './panelControl.module.scss';

interface ITriggerControlProps {
  trigger: ReactNode | ((isOpen: boolean) => ReactNode),
  control: ReactNode,
  className?: string,
  panelId?: string,
  useInteraction: (containerRef: React.RefObject<HTMLDivElement | null>) => IPanelInteraction,
}

const enhanceTrigger = (
  element: ReactNode,
  props: Record<string, unknown>,
): ReactNode => {
  if (!isValidElement(element)) {
    return element;
  }

  return cloneElement(element, { ...(element.props as Record<string, unknown>), ...props });
};

const TriggerControl = ({
  trigger,
  control,
  className = '',
  panelId = 'default',
  useInteraction,
}: ITriggerControlProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = `panel-menu-${ panelId }`;

  const { isPanelVisible, triggerProps, containerProps, panelRef } = useInteraction(containerRef);

  const triggerElement = typeof trigger === 'function' ? trigger(isPanelVisible) : trigger;

  return (
    <div
      ref={ containerRef }
      className={ cn(styles.panelControl, className) }
      data-panel-control
      { ...containerProps }
    >
      { enhanceTrigger(triggerElement, triggerProps) }
      <div
        ref={ panelRef }
        id={ menuId }
        role="region"
        aria-label={ `${ panelId } menu` }
        aria-hidden={ !isPanelVisible }
        inert={ !isPanelVisible }
        data-state={ isPanelVisible ? 'open' : 'closed' }
        className={ cn(styles.panel, { [styles.isOpen]: isPanelVisible }) }
      >
        { control }
      </div>
    </div>
  );
};

export default TriggerControl;
